import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getCookie, setCookie } from "../utils/cookies";
import useLanguages, { SUPPORTED_LANGUAGE_CODES } from "../constants/languages";

interface LanguageContextType {
  currentLanguage: string;
  changeLanguage: (lang: string) => void;
  availableLanguages: string[];
  getLocalizedPath: (path: string) => string;
  getCurrentLanguageDisplayName: () => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams();
  const [currentLanguage, setCurrentLanguage] = useState("az");
  const languages = useLanguages();

  const availableLanguages = useMemo<string[]>(
    () => [...SUPPORTED_LANGUAGE_CODES],
    [],
  );

  const getCurrentLanguageDisplayName = (): string => {
    const currentLang = languages.find((lang) => lang.code === currentLanguage);
    return currentLang ? currentLang.name : "AZ";
  };

  useEffect(() => {
    const initializeLanguage = () => {
      if (location.pathname === "/") {
        const cookieLang = getCookie("preferred_language");
        const browserLang = navigator.language.split("-")[0];
        const defaultLang =
          cookieLang && availableLanguages.includes(cookieLang)
            ? cookieLang
            : availableLanguages.includes(browserLang)
              ? browserLang
              : "az";

        navigate(`/${defaultLang}`, { replace: true });
        return;
      }
      if (lang && availableLanguages.includes(lang)) {
        setLanguage(lang);
        return;
      }
      const cookieLang = getCookie("preferred_language");
      const browserLang = navigator.language.split("-")[0];
      const defaultLang =
        cookieLang && availableLanguages.includes(cookieLang)
          ? cookieLang
          : availableLanguages.includes(browserLang)
            ? browserLang
            : "az";

      const currentPath = location.pathname;
      const pathWithoutInvalidLang = currentPath.replace(/^\/([^/]+)/, "");
      navigate(`/${defaultLang}${pathWithoutInvalidLang}`, { replace: true });
    };

    const setLanguage = (langCode: string) => {
      i18n.changeLanguage(langCode);
      setCurrentLanguage(langCode);
      setCookie("preferred_language", langCode, 30);
      localStorage.setItem("i18nextLng", langCode);
    };

    initializeLanguage();
  }, [lang, i18n, navigate, location.pathname, availableLanguages]);

  const changeLanguage = (newLang: string) => {
    if (availableLanguages.includes(newLang)) {
      const currentPath = location.pathname;
      const pathWithoutLang = currentPath.replace(/^\/(az|en|es|de)/, "");
      const newPath = `/${newLang}${pathWithoutLang || "/"}`;

      i18n.changeLanguage(newLang);
      setCurrentLanguage(newLang);
      setCookie("preferred_language", newLang, 30);
      localStorage.setItem("i18nextLng", newLang);
      navigate(newPath);
    }
  };

  const getLocalizedPath = (path: string): string => {
    return `/${currentLanguage}${path.startsWith("/") ? path : `/${path}`}`;
  };

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        changeLanguage,
        availableLanguages,
        getLocalizedPath,
        getCurrentLanguageDisplayName,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
